import { Skeleton, Tooltip, useSpinner } from '@chikara/ui';
import { formatCurrency } from '@chikara/utils';
import type { PeriodSummary } from '../../types/credits.types';
import beaconIconUrl from '../../assets/beacon-icon.svg';
import arrowTimelineIconUrl from '../../assets/arrow-timeline-icon.svg';
import questionTriggerIconUrl from '../../assets/question-trigger-icon.svg';
import styles from './PeriodSummarySection.module.css';
import { t } from '@/i18n';

export interface PeriodSummarySectionProps {
  data: PeriodSummary;
}

export function PeriodSummarySection({ data }: PeriodSummarySectionProps) {
  const { isSpinnerLoading } = useSpinner();
  const formatMXN = (value: number) =>
    formatCurrency(value, { locale: 'es-MX', currency: 'MXN' });

  return (
    <section className={styles.section} aria-labelledby="period-summary-title">
      <div className={styles.summaryRowSeparator} />
      <h2 id="period-summary-title" className={styles.sectionTitle}>
        {t('credits.periodSummaryTitle')}
      </h2>
      <div className={styles.summaryRowSeparator} />

      <div className={styles.periodLayout}>
        {isSpinnerLoading ? (
            <div className={styles.detailsColumn}>
                <h3 className={styles.detailsTitle}>
                    <Skeleton width="120px" height="18px" />
                </h3>
                <div className={styles.detailsCard}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem' }}>
                        <Skeleton width="100%" height="40px" />
                        <Skeleton width="100%" height="60px" />
                        <Skeleton width="120px" height="20px" />
                    </div>
                </div>
            </div>
        ) : (
            <div className={styles.detailsColumn}>
                <h3 className={styles.detailsTitle}>
                    {t('credits.detailsTitle')}
                </h3>
                <div className={styles.detailsCard}>
                    <div className={styles.detailGroup}>
                    <span className={styles.detailLabel}>
                        {t('credits.nextCutoffDate')}
                    </span>
                    <span className={styles.datePill}>{data.nextCutoffDate}</span>
                    </div>
                    <div className={`${styles.detailGroup} ${styles.payment}`}>
                    <span className={styles.detailLabel}>
                        {t('credits.paymentDueDate')}
                    </span>
                    <span className={styles.datePill}>{data.paymentDueDate}</span>
                    <p className={styles.helpCaption}>
                        {t('credits.noExtraCharges')}
                    </p>
                    </div>
                    <div className={styles.nonBusinessRow}>
                    <span className={styles.detailLabel}>
                        {t('credits.nonBusinessDays')}
                    </span>
                    <Tooltip content={t('credits.questionTooltip')} position="right">
                        <img
                            src={questionTriggerIconUrl}
                            alt={t('credits.nonBusinessDays')}
                            className={styles.questionIcon}
                        />
                    </Tooltip>
                    </div>
                </div>
            </div>
        )}
        <div className={styles.timelineColumn} aria-hidden="true">
          <span className={styles.timelineDateTop}>{data.periodStartDate}</span>
          <div 
            className={styles.timelineTrack}
            style={{ '--progress-percentage': `${data.progressPercentage}%` } as React.CSSProperties}
          >
            <div className={styles.timelineDots} />
            <img
              src={beaconIconUrl}
              alt="dot"
              className={styles.timelineBeacon}
            />
            <img
              src={arrowTimelineIconUrl}
              alt=""
              className={styles.timelineArrow}
            />
          </div>
          <span className={styles.timelineDateBottom}>{data.cutoffDate}</span>
        </div>
        {isSpinnerLoading ? (
            <div className={styles.summaryColumn}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem' }}>
                    <Skeleton width="100%" height="24px" />
                    <Skeleton width="100%" height="24px" />
                    <Skeleton width="100%" height="24px" />
                    <Skeleton width="100%" height="24px" />
                    <div className={styles.summaryRowSeparator} />
                    <Skeleton width="100%" height="60px" style={{ borderRadius: '12px' }} />
                </div>
            </div>
        ) : (
            <div className={styles.summaryColumn}>
                <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>
                    {t('credits.periodStart')}
                    </span>
                    <span className={styles.summaryValue}>
                    {formatMXN(data.periodStartBalance)}
                    </span>
                </div>
                <div className={styles.summaryRowSeparator} />
                <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>{t('credits.charges')}</span>
                    <span className={styles.summaryValue}>
                    {formatMXN(data.charges)}
                    </span>
                </div>
                <div className={styles.summaryRow}>
                    <span className={`${styles.summaryLabel} ${styles.creditsLabelGreen}`}>
                    {t('credits.credits')}
                    </span>
                    <span className={`${styles.summaryValue} ${styles.creditsPositive}`}>
                    {formatMXN(data.credits)}
                    </span>
                </div>
                <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>
                    {t('credits.interestAndFees')}
                    </span>
                    <span className={styles.summaryValue}>
                    {formatMXN(data.interestAndFees)}
                    </span>
                </div>
                <div className={styles.summaryRowSeparator} />
                <div className={styles.balanceRow}>
                    <div className={styles.balanceInfo}>
                    <span className={styles.balanceLabel}>
                        {t('credits.currentBalance')}
                    </span>
                    <span className={styles.balanceDate}>{data.balanceDate}</span>
                    </div>
                    <span className={styles.balancePill}>
                    {formatMXN(data.currentBalance)}
                    </span>
                </div>
                <div className={styles.cutoffRow}>
                    <div className={styles.cutoffInfo}>
                    <span className={styles.cutoffLabel}>
                        {t('credits.cutoffPeriod')}
                    </span>
                    <span className={styles.cutoffDays}>
                        {t('credits.cutoffInDays', { count: data.cutoffInDays })}
                    </span>
                    </div>
                </div>
            </div>
        )}
      </div>
    </section>
  );
}

PeriodSummarySection.displayName = 'PeriodSummarySection';
